import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, getStoredSession, isOtpChallenge, storeSession } from "../lib/api";
import { clearOtpChallenge } from "../lib/otpChallenge";
import type { LoginInput, LoginResult, Session } from "../types/api";

type AuthContextValue = {
  session: Session | null;
  isAuthenticated: boolean;
  isReady: boolean;
  signIn: (input: LoginInput) => Promise<LoginResult>;
  confirmOtp: (
    desafio: string,
    codigo: string,
    lembrarDispositivo: boolean,
  ) => Promise<Session>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(() => getStoredSession());

  useEffect(() => {
    const clearExpiredSession = () => setSession(null);
    window.addEventListener("mira:unauthorized", clearExpiredSession);
    return () => window.removeEventListener("mira:unauthorized", clearExpiredSession);
  }, []);

  const startSession = useCallback((nextSession: Session) => {
    clearOtpChallenge();
    storeSession(nextSession);
    setSession(nextSession);
    return nextSession;
  }, []);

  const signIn = useCallback(
    async (input: LoginInput) => {
      const result = await api.login(input);
      if (isOtpChallenge(result)) return result;
      return startSession(result);
    },
    [startSession],
  );

  const confirmOtp = useCallback(
    async (desafio: string, codigo: string, lembrarDispositivo: boolean) =>
      startSession(
        await api.validateLoginOtp(desafio, codigo, lembrarDispositivo),
      ),
    [startSession],
  );

  const signOut = useCallback(() => {
    clearOtpChallenge();
    storeSession(null);
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: Boolean(session?.token),
      isReady: true,
      signIn,
      confirmOtp,
      signOut,
    }),
    [confirmOtp, session, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro do AuthProvider.");
  return context;
}
